class CoralApi {
  getApiKey() {
    return process.env.REACT_APP_CORAL_SERVICE_API_KEY;
  }

  getWsBaseUrl() {
    return process.env.REACT_APP_CORAL_SERVICE_WS_BASE_URL;
  }

  async request(endpoint, organizationId, options = {}) {
    const base = process.env.REACT_APP_CORAL_SERVICE_BASE_URL;
    const url = new URL(base + endpoint, base);

    const { params, headers, ...rest } = options;
    if (params && typeof params === 'object') {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    try {
      const response = await fetch(url.toString(), {
        ...rest,
        headers: {
          'Content-Type': 'application/json',
          'x-org-id': organizationId,
          'x-api-key': this.getApiKey(),
          ...headers,
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Request failed' }));
        throw new Error(error.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Coral API Error: ${endpoint}`, error);
      throw error;
    }
  }

  // Groups Management
  async fetchGroups(organizationId, filter = "all") {
    return this.request('/groups', organizationId, {
      params: {
        filter,
      },
    });
  }

  async fetchThreadedGroups(organizationId) {
    return this.request('/threaded-groups', organizationId);
  }

  async fetchGroup(organizationId, groupId) {
    return this.request(`/groups/${groupId}`, organizationId);
  }

  async createGroup(organizationId, data) {
    return this.request('/groups', organizationId, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async editGroup(organizationId, groupId, data) {
    return this.request(`/groups/${groupId}`, organizationId, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteGroup(organizationId, groupId) {
    // return this.request(`/groups/${groupId}`, organizationId, {
    //   method: 'DELETE',
    // });

    // Delete group in backend is actually set to soft delete
    return this.request(`/groups/${groupId}/hidden`, organizationId, {
      method: 'PATCH',
      body: JSON.stringify({ hidden: true }),
    });
  }

  async fetchGroupParticipants(organizationId, groupId) {
    return this.request(`/groups/${groupId}/participants`, organizationId);
  }

  async addGroupParticipants(organizationId, groupId, participants) {
    return this.request(`/groups/${groupId}/participants`, organizationId, {
      method: 'POST',
      body: JSON.stringify({ participants }),
    });
  }

  async removeGroupParticipants(organizationId, groupId, phoneNumbers) {
    return this.request(`/groups/${groupId}/participants`, organizationId, {
      method: 'DELETE',
      body: JSON.stringify({ participants: phoneNumbers }),
    });
  }

  // Messages
  async fetchGroupMessages(organizationId, groupId, page = 1) {
    return this.request(`/groups/${groupId}/messages?page=${page}`, organizationId);
  }

  async sendGroupMessage(organizationId, groupId, message) {
    return this.request(`/groups/${groupId}/messages`, organizationId, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }

  async sendGroupTemplate(organizationId, groupId, templateName, parameters) {
    return this.request(`/groups/${groupId}/templates/${templateName}`, organizationId, {
      method: 'POST',
      body: JSON.stringify({ parameters }),
    });
  }

  // Alerts
  async fetchAllAlerts(organizationId) {
    return this.request('/alerts', organizationId);
  }

  async fetchAlert(organizationId, alertId) {
    return this.request(`/alerts/${alertId}`, organizationId);
  }

  async fetchGroupAlerts(organizationId, groupId) {
    return this.request(`/groups/${groupId}/alerts`, organizationId);
  }

  async sendAlertEmail(organizationId, alertId, recipientEmail, subject, body) {
    return this.request(`/alerts/${alertId}/send_email`, organizationId, {
      method: 'POST',
      body: JSON.stringify({ to: recipientEmail, subject, body }),
    });
  }

  // Organization settings
  async fetchOrganizationSettings(organizationId) {
    return this.request('/organizations', organizationId);
  }

  async updateOrganizationSettings(organizationId, organizationSettings) {
    return this.request('/organizations', organizationId, {
      method: 'PATCH',
      body: JSON.stringify(organizationSettings),
    });
  }
}

export default new CoralApi();
